import React from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {
  defineMessage,
  Plural,
  Trans,
} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {useBillingComposer} from '../../../settings/billing/common/hooks/billingHooksHelpers';
import {useBillingNavigateToPlanTab} from '../../../settings/billing/helpers';
import {contactSalesEmailAddress} from '../../common/callToAction/contactSalesButton';
import {BottomBar} from './bottomBar';
import {getBillingIsCancellingInfo} from './bottomBarSelectors';

const StyledTextDiv = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: ${fontWeights.semibold};
  display: flex;
  gap: 5px;
`;

const StyledWarningTextDiv = styled(StyledTextDiv)`
  display: flex;
  gap: 8px;
  color: ${(p) => p.theme.palette.red.shade40};
`;

/*
 * Component.
 */

export const SubscriptionCancellingBottomBar = () => {
  const {commitmentTerm, daysLeftBeforeCancel} = useAppSelector(getBillingIsCancellingInfo);

  const navigateToBilling = useBillingNavigateToPlanTab();
  const localize = useLocalize();
  const {compose} = useBillingComposer({
    recipients: [contactSalesEmailAddress],
    subject: localize(
      defineMessage({
        id: 'bottom-bar-canelling-contact-us-request',
        message: 'Request to renew subscription',
      }),
    ),
  });

  const isCancelled = daysLeftBeforeCancel <= 0;

  const renderText = () => (
    <StyledTextDiv>
      {isCancelled ? (
        <StyledWarningTextDiv>
          <Icon name="warningFilledSmall" />
          {renderCancelledText(commitmentTerm)}
        </StyledWarningTextDiv>
      ) : (
        <>
          <Plural
            id="bottom-bar-cancel-info"
            value={daysLeftBeforeCancel}
            one={renderIsCancellingSingleText(commitmentTerm)}
            other={renderIsCancellingMultiText(commitmentTerm)}
          />
          <TooltipCoordinator
            placement="top"
            persistent
            render={() => (
              <Trans id="bottom-bar-cancel-tooltip">
                Your account will be blocked when your subscription ends
              </Trans>
            )}
            isMultiline
          />
        </>
      )}
    </StyledTextDiv>
  );

  const renderActionButtons = () => (
    <>
      <Button
        size={VisualSizesEnum.SMALL}
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={() => navigateToBilling()}
      >
        <Trans id="bottom-bar-individual-renew-button">Renew now</Trans>
      </Button>
      <Button size={VisualSizesEnum.SMALL} onClick={compose} leftIcon="messageSmall">
        <Trans id="bottom-bar-individual-contact-button">Contact us</Trans>
      </Button>
    </>
  );

  return <BottomBar renderText={renderText} renderActionButtons={renderActionButtons} />;
};

const shouldDisplayAnnualText = (commitmentTerm: string | undefined) =>
  commitmentTerm === 'yearly' || (commitmentTerm && commitmentTerm.startsWith('multi'));

const renderCancelledText = (commitmentTerm: string | undefined) => {
  if (shouldDisplayAnnualText(commitmentTerm)) {
    return <Trans id="bottom-bar-expired-annually">Your annual subscription has expired</Trans>;
  }
  if (commitmentTerm === 'monthly') {
    return <Trans id="bottom-bar-expired-monthly">Your monthly subscription has expired</Trans>;
  }
  return <Trans id="bottom-bar-expired-none">Your subscription has expired</Trans>;
};

const renderIsCancellingSingleText = (commitmentTerm: string | undefined) => {
  if (shouldDisplayAnnualText(commitmentTerm)) {
    return 'Your annual subscription expires in 1 day';
  }
  if (commitmentTerm === 'monthly') {
    return 'Your monthly subscription expires in 1 day';
  }
  return 'Your subscription expires in 1 day';
};

const renderIsCancellingMultiText = (commitmentTerm: string | undefined) => {
  if (shouldDisplayAnnualText(commitmentTerm)) {
    return 'Your annual subscription expires in # days';
  }
  if (commitmentTerm === 'monthly') {
    return 'Your monthly subscription expires in # days';
  }
  return 'Your subscription expires in # days';
};
